import React, { useState, useEffect } from "react";
import JsonTreeViewer from "./JsonTreeViewer";
import { Button } from "@deere/isg.component-library"; // Assuming Button is available as seen in EngineeringTools

interface MQTTOrgExplorerProps {
  orgName: string;
  setOrgName: (name: string) => void;
  fullMQTT: any;
  devToolsOrgOverride: boolean;
  onRevertToHeaderOrg: () => void;
  headerOrgName: string;
}

const MQTTOrgExplorer: React.FC<MQTTOrgExplorerProps> = ({
  orgName,
  setOrgName,
  fullMQTT,
  devToolsOrgOverride,
  onRevertToHeaderOrg,
  headerOrgName,
}) => {
  const [orgList, setOrgList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");

  async function fetchOrgList() {
    setLoading(true);
    try {
      const response = await fetch("/API/MQTT/List");
      if (response.ok) {
        const list = await response.json();
        let tempOrgList: Array<string> = [""];
        if (Array.isArray(list)) {
          tempOrgList = [...tempOrgList, ...list];
        }
        setOrgList(tempOrgList);
      } else {
        console.error("Failed to fetch org list");
      }
    } catch (error) {
      console.error("Error fetching org list", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchOrgList();
  }, []);

  const copyDataToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(fullMQTT, null, 2));
  };

  const styles = `
        .mqtt-explorer-container {
            border: 1px solid #e1e4e8;
            border-radius: 6px;
            background-color: #ffffff;
            padding: 20px;
            margin-bottom: 20px;
        }
        .mqtt-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            flex-wrap: wrap;
            gap: 15px;
        }
        .mqtt-title {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
        }
        .mqtt-controls {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            align-items: center;
        }
        .mqtt-select {
            padding: 6px 12px;
            border-radius: 6px;
            border: 1px solid #d1d5da;
            font-size: 14px;
            min-width: 200px;
            max-width: 100%;
        }
        .mqtt-button {
            padding: 6px 12px;
            border-radius: 6px;
            border: 1px solid #d1d5da;
            background: #f6f8fa;
            cursor: pointer;
            font-size: 14px;
            display: flex;
            align-items: center;
            gap: 5px;
        }
        .mqtt-button:hover {
            background-color: #e1e4e8;
        }
        
        @media (max-width: 600px) {
            .mqtt-header {
                flex-direction: column;
                align-items: flex-start;
            }
            .mqtt-controls {
                width: 100%;
                flex-direction: column;
                align-items: stretch;
            }
            .mqtt-select {
                width: 100%;
                min-width: unset;
            }
            .mqtt-button {
                justify-content: center;
            }
        }
    `;

  return (
    <div className="mqtt-org-explorer mqtt-explorer-container">
      <style>{styles}</style>
      <div className="mqtt-header">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h2 className="mqtt-title">MQTT Organization Explorer</h2>
          <button
            className="mqtt-button"
            onClick={fetchOrgList}
            title="Refresh Organization List"
            style={{ padding: "4px 8px", fontSize: "12px" }}
          >
            {loading ? "..." : "↻"}
          </button>
        </div>

        <div className="mqtt-controls">
          <select
            className="mqtt-select"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
          >
            <option value="" disabled>
              Select Organization
            </option>
            {orgList.map((org, index) => (
              <option key={index} value={org}>
                {org || "-- Select --"}
              </option>
            ))}
          </select>

          {devToolsOrgOverride && (
            <button
              className="mqtt-button"
              onClick={onRevertToHeaderOrg}
              title={`Revert to header org: ${headerOrgName}`}
            >
              ↩ Use Header Org
            </button>
          )}

          {orgName && (
            <button className="mqtt-button" onClick={copyDataToClipboard}>
              Copy JSON
            </button>
          )}
        </div>
      </div>

      {orgName ? (
        <div
          style={{
            border: "1px solid #e1e4e8",
            borderRadius: "6px",
            padding: "10px",
            backgroundColor: "#f6f8fa",
            overflowX: "auto",
          }}
        >
          <JsonTreeViewer data={fullMQTT} initialExpanded={true} />
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#586069",
            backgroundColor: "#fafbfc",
            border: "1px dashed #e1e4e8",
            borderRadius: "6px",
          }}
        >
          Please select an organization to view data.
        </div>
      )}
    </div>
  );
};

export default MQTTOrgExplorer;
